import { RefObject, useEffect, useState } from 'react';

import { useOnScreen } from '@/common/hooks/useOnScreen/useOnScreen';
import { useImpressionContext } from '@/common/impressions/useImpressionContext';

import { BaseMessage, shouldMessageSend } from './common';

export interface UseSliderImpressionOptions {
  threshold?: number;
  delay?: number;
  slideThreshold?: number;
}

type UseSliderImpression = <Message extends BaseMessage>(
  sliderRef: RefObject<Element>,
  options?: UseSliderImpressionOptions,
) => UseSlideImpression<Message>;

export type UseSlideImpression<Message extends BaseMessage> = (
  slideRef: RefObject<Element>,
  message: Message,
  options?: {
    onHold?: boolean;
  },
) => { onHold: boolean; setOnHold: (isOnHold: boolean) => void };

const SLIDE_THRESHOLD = 0.01;

export const useSliderImpression: UseSliderImpression = <Message extends BaseMessage>(
  sliderRef: RefObject<Element>,
  {
    threshold = 0.5,
    delay = 1000,
    slideThreshold = SLIDE_THRESHOLD,
  }: UseSliderImpressionOptions = {},
) => {
  const isSliderOnScreen = useOnScreen(sliderRef, { threshold, delay });
  const useSlideImpression: UseSlideImpression<Message> = (slideRef, message, options) => {
    const worker = useImpressionContext();
    const [onHold, setOnHold] = useState(options?.onHold || false);

    const [slidesWithEventSent, setSlidesWithEventSent] = useState<string[]>([]);

    const isSlideOnScreen = useOnScreen(slideRef, {
      threshold: slideThreshold,
      once: true,
      onHold,
    });

    useEffect(() => {
      if (
        shouldMessageSend(message) &&
        isSliderOnScreen &&
        isSlideOnScreen &&
        worker &&
        !slidesWithEventSent.includes(JSON.stringify(message))
      ) {
        setSlidesWithEventSent((events) => [...events, JSON.stringify(message)]);
        worker.postMessage(message);
      }
    }, [worker, isSlideOnScreen, slideRef, message, slidesWithEventSent]);
    return { onHold, setOnHold };
  };

  return useSlideImpression;
};
